import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, TextField, MenuItem, Select, InputLabel, FormControl,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert, Button, IconButton, Tooltip,
} from "@mui/material";
import { Logout, History as HistoryIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatEST } from "../dateUtils";
import ThemeToggle from "./ThemeToggle";
// 2026-10-13 addition — UI for the 3 admin actions that existed on the
// backend already (reset-credentials/change-email/resend-patient-consent)
// but had no frontend caller anywhere in this app. Patients only ever
// appear in THIS table (AdminClinicians.js is clinician-only), so reset-
// credentials/change-email have to live here for patient accounts; the
// resend-consent tool is a stopgap (see AdminUserRecoveryActions.js's own
// header comment for why — this app has no admin order-detail view to
// put it on).
import { ResetCredentialsButton, ChangeEmailButton, ResendPatientConsentTool } from "./AdminUserRecoveryActions";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const BRAND = { navy: "#00357a", gold: "#c9a96e" };

const PAYMENT_STATUS_COLOR = {
  completed: "success",
  pending: "warning",
};

// 2026-10-05 addition (plan A.7) — payment_preference was rendered as the
// raw DB value ("now"/"later") with no label map at all (the sibling
// labariumaireport app's internal-notification email already maps this
// correctly as "Pay now"/"Pay later" — same label here).
const PAYMENT_PREFERENCE_LABEL = { now: "Pay now", later: "Pay later" };

// 2026-10-08 addition (audit click-through) — the Audit Dashboard itself
// physically lives in labariumaireport's frontend (AuditDashboard.js, route
// /admin/audit-log), reusing its existing portal-audit-log tab rather than
// building a second one here, so a row here has to link out cross-app.
// REACT_APP_REPORT_ADMIN_URL is documented (.env.example) as "base URL of
// the deployed labariumaireport frontend's admin login page" — i.e. a full
// page URL, not necessarily a bare origin (it's used as-is for the
// existing "Switch to Report Admin →" button below). Building the link by
// hand from that value (origin + /admin/audit-log) would double up any
// path already in it (e.g. .../admin-login/admin/audit-log), so this
// extracts just the origin via the URL constructor instead.
function reportAuditLogUrl(identity) {
  const base = process.env.REACT_APP_REPORT_ADMIN_URL;
  if (!base) return null;
  let origin;
  try {
    origin = new URL(base).origin;
  } catch {
    return null; // malformed env value — fail closed, same as the existing link being hidden when unset
  }
  return `${origin}/admin/audit-log?source=portal&identity=${encodeURIComponent(identity)}`;
}

function StatTile({ label, value, isDark }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 140, p: 2, borderRadius: 2,
      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#fff",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
    }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.05em",
        color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
        {value}
      </Typography>
    </Box>
  );
}

function AdminDashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [paymentPrefFilter, setPaymentPrefFilter] = useState("all");

  const admin = JSON.parse(localStorage.getItem("admin") || "null");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API_BASE}/admin/users`, {
          headers: { Authorization: `Bearer ${admin?.token}` },
        });
        setUsers(res.data.users || []);
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("admin");
          navigate("/admin-login", { replace: true });
          return;
        }
        setError(extractErrorDetail(err, "Failed to load users."));
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("admin");
    navigate("/admin-login", { replace: true });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (paymentStatusFilter !== "all" && (u.payment_status || "") !== paymentStatusFilter) return false;
      if (paymentPrefFilter !== "all" && (u.payment_preference || "") !== paymentPrefFilter) return false;
      if (q) {
        const hay = `${u.name || ""} ${u.email || ""} ${u.phone || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter, paymentStatusFilter, paymentPrefFilter]);

  const stats = useMemo(() => {
    const patients = users.filter((u) => u.role === "patient");
    const clinicians = users.filter((u) => u.role === "clinician");
    const paid = patients.filter((u) => u.payment_status === "completed").length;
    const pending = patients.filter((u) => u.payment_status === "pending").length;
    return {
      total: users.length,
      patients: patients.length,
      clinicians: clinicians.length,
      paid,
      pending,
    };
  }, [users]);

  const bg = isDark ? "#080d1a" : "#f4f7ff";

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg }}>
      {/* 2026-10-05 fix — real gap, reported directly: this admin page
          looked visually inconsistent with labariumaireport's own admin
          side, which already carries the real Labarium navy+gold navbar
          (logo, theme toggle, logout) on every admin page. This app's own
          Login.js already uses the exact same navbar — it just never made
          it onto this post-login admin page. Reproduced verbatim from
          labariumaireport's AdminDashboard.js so both apps' admin sides
          match, not just their login screens. */}
      <Box sx={{
        bgcolor: "rgba(0,14,36,0.96)", backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(201,169,110,0.2)",
      }}>
        <Box sx={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.8) 30%, rgba(223,192,138,0.9) 50%, rgba(201,169,110,0.8) 70%, transparent)" }} />
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 60,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
          <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ThemeToggle />
            <Tooltip title="Log out">
              <IconButton size="small" onClick={handleLogout}
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", rowGap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.12em", color: BRAND.gold, mb: 0.5 }}>
            ADMIN
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
            User Dashboard
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          {/* 2026-10-12 addition — admin-created clinician accounts live on
              their own page (grouped by practice), same nav convention as
              every other admin view in this app (a Button that navigates
              to a separate AdminProtectedRoute-wrapped route). */}
          <Button variant="outlined" onClick={() => navigate("/admin-clinicians")}
            sx={{ textTransform: "none", fontWeight: 700, borderColor: BRAND.navy, color: BRAND.navy }}>
            Clinicians
          </Button>
          {/* 2026-10-23 addition (clinician invite-only build, Piece 6) —
              same nav convention as the Clinicians button above. */}
          <Button variant="outlined" onClick={() => navigate("/admin-sales-reps")}
            sx={{ textTransform: "none", fontWeight: 700, borderColor: BRAND.navy, color: BRAND.navy }}>
            Sales Reps
          </Button>
          <ResendPatientConsentTool token={admin?.token} />
          {/* 2026-10-04 — the RCM/payment dashboard, charts, and resend-
              payment-link action all now live on labariumaireport's
              consolidated admin side (see ADMIN_RCM_CONSOLIDATION_PLAN.md)
              — this page stays as a dormant fallback, same ADMIN_EMAIL/
              ADMIN_PASSWORD value, separate session. This link just saves
              remembering the other app's URL. */}
          {process.env.REACT_APP_REPORT_ADMIN_URL && (
            <Button variant="text" href={process.env.REACT_APP_REPORT_ADMIN_URL}
              target="_blank" rel="noopener noreferrer"
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem", color: "#888" }}>
              Switch to Report Admin →
            </Button>
          )}
          {/* 2026-10-05 — logout moved to the navy navbar above (matching
              report's own pattern); removed the duplicate text button that
              used to live here. */}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <StatTile label="Total users" value={stats.total} isDark={isDark} />
        <StatTile label="Patients" value={stats.patients} isDark={isDark} />
        <StatTile label="Clinicians" value={stats.clinicians} isDark={isDark} />
        <StatTile label="Paid" value={stats.paid} isDark={isDark} />
        <StatTile label="Payment pending" value={stats.pending} isDark={isDark} />
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <TextField
          size="small" label="Search name, email, phone"
          value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Role</InputLabel>
          <Select label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <MenuItem value="all">All roles</MenuItem>
            <MenuItem value="patient">Patient</MenuItem>
            <MenuItem value="clinician">Clinician</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Payment status</InputLabel>
          <Select label="Payment status" value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)}>
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Payment preference</InputLabel>
          <Select label="Payment preference" value={paymentPrefFilter} onChange={(e) => setPaymentPrefFilter(e.target.value)}>
            <MenuItem value="all">All preferences</MenuItem>
            <MenuItem value="now">Pay now</MenuItem>
            <MenuItem value="later">Pay later</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>DOB</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Payment preference</TableCell>
              <TableCell>Payment status</TableCell>
              <TableCell>Paid at</TableCell>
              <TableCell>Audit</TableCell>
              <TableCell>Account</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((u, i) => {
              const auditUrl = u.email ? reportAuditLogUrl(u.email) : null;
              return (
              <TableRow key={u.sub || u.email || i} hover>
                <TableCell>{u.name || "—"}</TableCell>
                <TableCell>{u.email || "—"}</TableCell>
                <TableCell>{u.phone || "—"}</TableCell>
                <TableCell>
                  <Chip size="small" label={u.role} color={u.role === "clinician" ? "info" : "default"} />
                </TableCell>
                <TableCell>{u.role === "patient" ? (u.date_of_birth || "—") : "—"}</TableCell>
                <TableCell>{formatEST(u.created_at, { dateOnly: true })}</TableCell>
                <TableCell>{u.role === "patient" ? (PAYMENT_PREFERENCE_LABEL[u.payment_preference] || u.payment_preference || "—") : "—"}</TableCell>
                <TableCell>
                  {u.role === "patient" && u.payment_status ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Chip size="small" label={u.payment_status} color={PAYMENT_STATUS_COLOR[u.payment_status] || "default"} />
                      {/* 2026-10-18 fix (admin field-visibility sweep) — a
                          failed charge or receipt-fetch failure was recorded
                          by the webhooks but never surfaced here, so admin
                          had no way to see it short of reading the DB directly. */}
                      {(u.payment_failed_reason || u.receipt_fetch_failed_reason) && (
                        <Tooltip title={[
                          u.payment_failed_reason ? `Payment failed: ${u.payment_failed_reason}` : null,
                          u.payment_failed_at ? `at ${formatEST(u.payment_failed_at)}` : null,
                          u.receipt_fetch_failed_reason ? `Receipt fetch failed: ${u.receipt_fetch_failed_reason}` : null,
                        ].filter(Boolean).join(" · ")}>
                          <Chip size="small" label="Issue" color="warning" variant="outlined" />
                        </Tooltip>
                      )}
                    </Box>
                  ) : "—"}
                </TableCell>
                <TableCell>
                  {formatEST(u.payment_confirmed_at)}
                </TableCell>
                <TableCell>
                  {/* 2026-10-08 addition (audit click-through) — cross-app
                      link to labariumaireport's AuditDashboard, pre-filtered
                      to this person's email (the identity value portal's own
                      record_audit_event logs for registration/login/payment;
                      the sibling app's get_portal_audit_log also resolves the
                      matching sub for the few clinician-only actions logged
                      under sub instead, so this one link still surfaces
                      those). Hidden when REACT_APP_REPORT_ADMIN_URL isn't
                      configured, same as the existing "Switch to Report
                      Admin" link above. */}
                  {auditUrl && (
                    <Tooltip title="View audit log">
                      <IconButton size="small" href={auditUrl} target="_blank" rel="noopener noreferrer">
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {/* 2026-10-13 addition — reset-credentials/change-email
                      had ZERO frontend caller anywhere in this app.
                      Disabled (not hidden) when `u.sub` is missing/empty —
                      the backend defaults "sub" to "" for any row that
                      predates the 2026-10-13 addition of that field, and
                      POSTing to /admin/users//reset-credentials (empty
                      path segment) would be a confusing 404 rather than a
                      clear "unavailable" state. */}
                  <Box sx={{ display: "flex", gap: 0.25 }}>
                    <ResetCredentialsButton sub={u.sub} email={u.email} token={admin?.token} iconOnly />
                    <ChangeEmailButton sub={u.sub} email={u.email} token={admin?.token} iconOnly
                      onChanged={(newEmail) => setUsers((prev) => prev.map((x) => (x.sub && x.sub === u.sub ? { ...x, email: newEmail } : x)))} />
                  </Box>
                </TableCell>
              </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                  No users match the current filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>
    </Box>
  );
}

export default AdminDashboard;
